import { useLayoutEffect, useRef } from 'react';
import type { RefObject } from 'react';
import { usePrefersReducedMotion } from './usePrefersReducedMotion';

export function useFlip(
  container: RefObject<HTMLElement | null>,
  selector: string,
  signature: unknown,
  durationMs = 450,
): void {
  const reduced = usePrefersReducedMotion();
  const positions = useRef(new WeakMap<Element, { x: number; y: number }>());
  const seen = useRef(false);

  useLayoutEffect(() => {
    const root = container.current;
    if (!root) return;
    const nodes = Array.from(root.querySelectorAll(selector));
    const next = new WeakMap<Element, { x: number; y: number }>();
    const visible = root.getBoundingClientRect();
    const onScreen = visible.bottom > 0 && visible.top < window.innerHeight;
    for (const node of nodes) {
      const box = node.getBoundingClientRect();
      next.set(node, { x: box.left, y: box.top });
      if (!seen.current || reduced || !onScreen) continue;
      const before = positions.current.get(node);
      const el = node as HTMLElement;
      if (before === undefined) {
        el.animate([{ opacity: 0 }, { opacity: 1 }], {
          duration: durationMs,
          easing: 'cubic-bezier(0.2, 0, 0, 1)',
        });
        continue;
      }
      const dx = before.x - box.left;
      const dy = before.y - box.top;
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1) continue;
      el.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'translate(0, 0)' }], {
        duration: durationMs,
        easing: 'cubic-bezier(0.2, 0, 0, 1)',
      });
    }
    positions.current = next;
    seen.current = true;
  }, [container, selector, signature, reduced, durationMs]);
}
